import React, { useMemo } from 'react'
import styled from 'styled-components'

import FormatCurrency from 'components/Common/FormatCurrency'
import Image from 'components/Common/Image'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import StickyPromptCard from 'components/Common/StickyPromptCard/StickyPromptCard'
import TextButton from 'components/Luxkit/Button/TextButton'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import CaptionTextBlock from 'components/Luxkit/TextBlocks/CaptionTextBlock'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import Group from 'components/utils/Group'
import { rem } from 'polished'
import noop from 'lib/noop'
import { CarHireReengagementView } from 'components/Common/StickyPromptCard/useCarHireReengagementSelectionLogic'
import { setCarHireReengagementDismissedInStorage } from 'storage/carHireReengagement'
import qs from 'qs'

const MainContent = styled(Group)`
  flex: 1;
`

const CarImage = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  filter: brightness(1.25);
  height: 100%;
  padding: ${rem(16)} 0;
`

interface Props {
  onClose?: () => void
  show: boolean
  stickyTop?: number
  carHireReengagementView: CarHireReengagementView
}

function CarHireReengagementCard({
  carHireReengagementView,
  show,
  stickyTop,
  onClose = noop,
}: Props) {
  const onClick = () => {
    setCarHireReengagementDismissedInStorage('clicked')
    onClose()
  }

  const onDismiss = () => {
    setCarHireReengagementDismissedInStorage('closed')
    onClose()
  }

  const vehicleImage = useMemo(() => ({
    ...carHireReengagementView.offerSummary?.vehicle.image,
    // modify the image URL to utilise car trawlers image options for a better image
    // 'secondary' faces left, 'primary' faces right
    url: `${carHireReengagementView.offerSummary?.vehicle.image.url?.replace('primary', 'secondary')}?w=68&dpr=2`,
  }), [carHireReengagementView])

  const queryParams = {
    pickUpName: carHireReengagementView.placeName,
    dropOffName: carHireReengagementView.placeName,
    pickUpId: carHireReengagementView.filters.pickUpLocationId,
    dropOffId: carHireReengagementView.filters.dropOffLocationId,
    pickUpDate: carHireReengagementView.filters.pickUpDate,
    pickUpTime: carHireReengagementView.filters.pickUpTime,
    dropOffDate: carHireReengagementView.filters.dropOffDate,
    dropOffTime: carHireReengagementView.filters.dropOffTime,
    ageCategory: carHireReengagementView.filters.driversAgeCategory,
    age: carHireReengagementView.filters.driversAge,
    pickUpSearchType: carHireReengagementView.filters.pickUpSearchType,
    dropOffSearchType: carHireReengagementView.filters.dropOffSearchType,
    isPickUpAirport: carHireReengagementView.filters.isPickUpAtAirport,
  }

  return (
    <StickyPromptCard
      onClose={onDismiss}
      to={`/search/car-hire?${qs.stringify(queryParams)}`}
      onClick={onClick}
      show={show}
      stickyTop={stickyTop}
    >
      <StickyPromptCard.Header>
        <Heading variant="heading6">Need to hire a car in {carHireReengagementView.placeName}?</Heading>
      </StickyPromptCard.Header>
      <StickyPromptCard.Content>
        <MainContent
          horizontalAlign="space-between"
          direction="horizontal"
          gap={16}
        >
          <VerticalSpacer gap={4}>
            <BodyText weight="bold" variant="medium" lineClamp={2}>
              We’ve found {carHireReengagementView.offerCount} cars
            </BodyText>
            <CaptionTextBlock variant="medium">
              {carHireReengagementView.pickUpLocationName} - {carHireReengagementView.dropOffLocationName}
            </CaptionTextBlock>
          </VerticalSpacer>
          <CarImage>
            <Image image={vehicleImage} width={69} />
          </CarImage>
        </MainContent>
      </StickyPromptCard.Content>
      <StickyPromptCard.Footer>
        <VerticalSpacer gap={16}>
          <Group
            direction="horizontal"
            gap={12}
            smGap={16}
            horizontalAlign="space-between"
            verticalAlign="end"
          >
            <Group direction="vertical">
              <CaptionTextBlock variant="medium">
                Total for {carHireReengagementView.offerSummary?.duration} days from
              </CaptionTextBlock>
              <BodyTextBlock variant="large" weight="bold">
                <FormatCurrency value={carHireReengagementView.offerSummary?.totalCharge.price || 0} />
              </BodyTextBlock>
            </Group>

            <TextButton
              variant="default"
              kind="primary"
              size="small"
              nonInteractable
            >
              View Cars
            </TextButton>
          </Group>
        </VerticalSpacer>
      </StickyPromptCard.Footer>
    </StickyPromptCard>
  )
}

export default CarHireReengagementCard
